import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { toastActions } from 'redux/toasts';
import { loadingActions } from 'redux/loading';
import { store } from 'redux/store';
import { APIError, ApiErrorResponseData } from 'models/apiError';
import { ToastContent, ToastLevel } from 'models/toasts';
import { loadingTimeout } from 'constants/timeout';
import { maintenanceActions } from 'redux/maintenance';
import { ApiHeaders } from 'models/apiHeaders';
import { ApiOtherOptions } from 'models/apiOtherOptions';
import { authActions } from 'redux/auth';

type AxiosConfigExtraProps = {
  requestId: number;
};

const { REACT_APP_API_URL } = process.env;

const requests: number[] = [];
let currentIndex = 0;
let loadingTimeoutId: NodeJS.Timeout;
const ignoreUrlPatterns: RegExp[] = [
  /^\/time-lapse-viewer-service\/cameras\/attributes/,
]; // loading中表示不要なURLパターンがあればここに指定する

const requestFunc = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  const baseURL = REACT_APP_API_URL;
  try {
    const ignore = ignoreUrlPatterns.some(
      pattern => config.url && pattern.test(config.url)
    );

    const ignorePathPatterns = [
      /^\/time_lapse\/\d+\/time_series_heatmap/,
      /^\/time_lapse\/\d+\/time_series_nomal/,
    ];
    // 現在の画面のパターン
    const pathIgnore = ignorePathPatterns.some(
      pattern => config.url && pattern.test(window.location.pathname)
    );

    if (!ignore && !pathIgnore) {
      const requestId = currentIndex++;
      (config as AxiosRequestConfig & AxiosConfigExtraProps).requestId =
        requestId;
      store.dispatch(loadingActions.setLoading(true));
      requests.push(requestId);
    }

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    if (!token) throw Error;
    const url = config.url;
    if (config.headers) {
      config.headers['Authorization'] = token;
    }
    return {
      ...config,
      url,
      baseURL,
    };
  } catch (_) {
    store.dispatch(loadingActions.setLoading(false));
    return { ...config, baseURL };
  }
};

const responseErrorFunc = (
  error: AxiosError<ApiErrorResponseData>,
  customToast?: ToastContent
) => {
  const index = requests.indexOf(
    (error.config as InternalAxiosRequestConfig & AxiosConfigExtraProps)
      .requestId
  );
  if (index >= 0) {
    requests.splice(index, 1);
  }
  store.dispatch(loadingActions.setLoading(false));
  // エラーの場合に Store にエラー情報登録
  const { response } = error;
  // 以下のエラーパターンの場合はログアウトし、再ログイン要求
  // 1. 401 エラー : トークン切れ
  // 2. 404 エラーで `E403` から始まる : 権限切れ -> ログイン後ホーム画面に戻る
  let isNeedLogout = false;
  let isNeedMoveToRoot = false;
  const toastContents: ToastContent[] = [];
  if (
    response &&
    response.status > 0 &&
    response.data &&
    response.data.errors
  ) {
    // API 側で 401 レスポンスデータが返るようになったことを確認する
    // ログアウトパターン 1. のケア
    if (response.status === 401) {
      isNeedLogout = true;
    }

    response.data.errors.forEach((error: APIError) => {
      // ログアウトパターン 2. のケア
      if (response.status === 404 && error.error_code.startsWith('E403')) {
        isNeedLogout = true;
        isNeedMoveToRoot = true;
      }
      // 503
      if (response.status === 503) {
        switch (error.error_code) {
          case 'E503FFF':
            // メンテナンス画面
            store.dispatch(maintenanceActions.setMaintenanceFlags(true));
            break;
          default:
            if (customToast) {
              toastContents.push({
                ...customToast,
                message: customToast.message
                  ? customToast.message
                  : error.message,
              });
            } else {
              toastContents.push({
                message: error.message,
                level: ToastLevel.validationError,
              });
            }
            break;
        }
      } else {
        if (customToast) {
          toastContents.push({
            ...customToast,
            message: customToast.message ? customToast.message : error.message,
          });
        } else {
          toastContents.push({
            message: error.message,
            level: ToastLevel.validationError,
          });
        }
      }
    });
  }
  // TODO: 401 エラーの際にも response が返ってくるようになれば削除する
  // 401 エラーの代替実装
  // undefined 判定しているため、他の未考慮エラーケースも引っかかる可能性あり
  if (response === undefined) {
    isNeedLogout = true;
  }

  // LayoutContainer でのログアウト処理をするために、フラグを設定
  if (isNeedLogout) {
    store.dispatch(authActions.logout(isNeedMoveToRoot));
  }

  // エラー表示
  if (toastContents.length > 0 && response && response.status >= 500) {
    toastContents[0].level = ToastLevel.serverError;
    store.dispatch(toastActions.appendToasts(toastContents));
  } else if (toastContents.length > 0) {
    store.dispatch(toastActions.appendToasts(toastContents));
  }
  return Promise.reject(error);
};

const responseWatchFunc = (response: AxiosResponse) => {
  const index = requests.indexOf(
    (response.config as AxiosRequestConfig & AxiosConfigExtraProps).requestId
  );

  if (index >= 0) {
    requests.splice(index, 1);
    if (requests.length === 0) {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
      }
      loadingTimeoutId = setTimeout(() => {
        store.dispatch(loadingActions.setLoading(false));
      }, loadingTimeout);
    }
  }

  return response;
};

// デフォルトコンフィグ
const defaultAxiosRequestConfig = {
  headers: {
    Accept: 'application/json',
  },
};

// デフォルト API クライアント
const apiClient = axios.create(defaultAxiosRequestConfig);

// 共通リクエスト
apiClient.interceptors.request.use(requestFunc);

// 共通エラーケース
apiClient.interceptors.response.use(responseWatchFunc, responseErrorFunc);

// ヘッダーを独自に付与するカスタム API クライアント
export const generateCustomApiClient = (
  heaaders: ApiHeaders,
  otherOptions:
    | ApiOtherOptions
    | {
        //
      },
  isNecessaryBaseURL = true,
  isNecessaryRequestInterceptors = true,
  customToast?: ToastContent
) => {
  const otherOptionsBranch = otherOptions
    ? otherOptions
    : {
        //
      };
  const params: AxiosRequestConfig = {
    baseURL: isNecessaryBaseURL ? REACT_APP_API_URL : undefined,
    headers: {
      ...heaaders,
    },
    ...otherOptionsBranch,
  };
  const customApiClient = axios.create(params);

  // 共通リクエスト
  if (isNecessaryRequestInterceptors) {
    customApiClient.interceptors.request.use(requestFunc);
  }

  // 共通エラーケース
  customApiClient.interceptors.response.use(
    response =>
      isNecessaryRequestInterceptors ? responseWatchFunc(response) : response,
    error => responseErrorFunc(error, customToast)
  );

  return customApiClient;
};

// エラートーストのカスタム API クライアント
export const customToastApiClient = (customToast: ToastContent) => {
  const customApiClient = axios.create(defaultAxiosRequestConfig);
  customApiClient.interceptors.request.use(requestFunc);
  customApiClient.interceptors.response.use(responseWatchFunc, error =>
    responseErrorFunc(error, customToast)
  );

  return customApiClient;
};

export default apiClient;
